import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { Button, ChatColumn, Heading } from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { useSiteInviteForm } from '../Site';

/**
 * What ends a site's setup conversation once the site is published: an offer to invite members to
 * it, and where to go from here.
 */
export function SiteSetupEnd(props: {
    organizationId: string;
    siteId: string;
    onOpenConversation: () => void;
    onOpenSite: () => void;
}) {
    const { organizationId, siteId, onOpenConversation, onOpenSite } = props;
    const { data: site } = useQuery(apiQuery('getSiteById', { organizationId, siteId }));
    const [inviteEnded, setInviteEnded] = React.useState(false);

    return (
        <ChatColumn className="gap-3 pb-4">
            {/* Inviting to a site needs admin access to it. */}
            {inviteEnded || !site?.permissions.admin ? null : (
                <React.Suspense fallback={null}>
                    <SiteSetupInviteCard
                        organizationId={organizationId}
                        site={site}
                        onEnded={() => setInviteEnded(true)}
                    />
                </React.Suspense>
            )}
            <SiteSetupEndCard
                icon={Icon.AIAgent}
                title="From now on, ask the GitBook Agent"
                className="bg-neutral-surface-subtle"
                description="It can do everything from setup on request: change the theme, add a domain, connect Git, invite people, or rewrite a page. Setup leaves the sidebar now."
                actions={
                    <>
                        <Button
                            kind="primary"
                            size="small"
                            leadingIcon={Icon.AIAgent}
                            label="Open new conversation"
                            onPress={onOpenConversation}
                        />
                        <Button
                            kind="secondary"
                            size="small"
                            label="Go to site overview"
                            onPress={onOpenSite}
                        />
                    </>
                }
            />
        </ChatColumn>
    );
}

/** The site's invite form, as the site's invite dialog shows it. */
function SiteSetupInviteCard(props: {
    organizationId: string;
    site: api.Site;
    onEnded: () => void;
}) {
    const { organizationId, site, onEnded } = props;
    const form = useSiteInviteForm({ organizationId, site, onInvited: onEnded });

    return (
        <SiteSetupEndCard
            title="Invite members to this site"
            className="bg-base"
            description="Optional. Invite the people who write or maintain these docs to work on them with you."
            actions={
                <>
                    <Button
                        kind="primary"
                        size="small"
                        leadingIcon={Icon.Users}
                        label="Invite"
                        disabled={!form.canInvite}
                        onPress={form.invite}
                    />
                    <Button kind="secondary" size="small" label="Skip" onPress={onEnded} />
                </>
            }
        >
            {form.fields}
        </SiteSetupEndCard>
    );
}

function SiteSetupEndCard(props: {
    icon?: SVGIconComponent;
    title: string;
    description: string;
    actions: React.ReactNode;
    children?: React.ReactNode;
    className: string;
}) {
    const { icon: CardIcon, title, description, actions, children, className } = props;

    return (
        <div
            className={clsx(
                'flex flex-col gap-3 rounded-lg border border-neutral-subtle p-3',
                className
            )}
        >
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    {CardIcon ? <CardIcon className="size-4 shrink-0" /> : null}
                    <Heading level={3} className="text-ui-base font-semibold">
                        {title}
                    </Heading>
                </div>
                <p className="text-ui-base text-muted">{description}</p>
            </div>
            {children}
            <div className="flex flex-wrap items-center gap-2">{actions}</div>
        </div>
    );
}
